<template>
  <CCard class="my-4">
    <div class="border-bottom">
      <CRow class="d-flex px-5 pt-4 mb-3 justify-content-between align-items-center">
        <div>
          <p class="font-3xl font-weight-bold mb-3">{{ $t('automation.statistics.key-metrics.title') }}</p>
          <span class="text-gray-500">{{ $t('automation.statistics.key-metrics.description.selected-period') }}</span>
        </div>
        <span>{{ periodStartFormatted }} {{ $t('automation.statistics.chart.period.complement') }} {{ periodEndFormatted }}</span>
      </CRow>
      <CRow class="px-5 mb-2 justify-content-end">
        <CCol sm="2" class="p-0">
          <v-select
            class="no-close border-gray-300 rounded select"
            :placeholder="$t('automation.statistics.chart.period.placeholder')"
            :options="periodOptions"
            :searchable="false"
            :disabled="isLoadingData"
            v-model="selectedPeriod"
          />
        </CCol>
      </CRow>
    </div>
    <CContainer v-if="isLoadingData" class="d-flex h-100 justify-content-center align-items-center my-5">
      <CSpinner style="width: 60px; height: 60px" />
    </CContainer>
    <div class="h-100" v-else>
      <div v-if="hasChartData">
        <CRow class="mt-4 px-5 mb-4 justify-content-start">
          <CCol sm="6" class="d-flex p-0 gap-20">
            <div v-for="dataset in chartData.datasets" class="d-flex flex-column border-right-short pr-4 w-130px">
              <span class="font-3xl font-weight-bold">{{ getDatasetCount(dataset) }}</span>
              <span class="text-gray-500">{{ dataset.label }}</span>
            </div>
          </CCol>
        </CRow>
        <CChartBar class="px-4 mb-4" :labels="chartData.labels" :datasets="chartData.datasets" :options="chartConfig" />
      </div>
      <div v-else class="my-4 d-flex justify-content-center">
        <EmptyMessage />
      </div>
    </div>
  </CCard>
</template>
<script>
import vSelect from 'vue-select'
import { STATISTICS_CHART_PERIOD_TYPES } from '../../constants'
import { DateTime } from 'luxon'
import { dateFormat, getUserDateFormat } from '@/utils/dateUtil.js'
import EmptyMessage from '@/components/EmptyMessage.vue'
import { CChartBar } from '@coreui/vue-chartjs'
import { getGroupedChartData } from '../AutomationStatisticsChart/useAutomationStatisticsChart'

export default {
  name: 'AutomationStatisticsChart',
  components: { vSelect, EmptyMessage, CChartBar },
  props: {
    statisticsProperties: {
      type: Array,
      default: () => [],
    },
    isLoadingData: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedPeriod: undefined,
      chartConfig: {
        legend: {
          display: true,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                // Remove casas decimais dos valores no eixo Y
                userCallback: function (label) {
                  if (Math.floor(label) === label) {
                    return label
                  }
                },
              },
            },
          ],
        },
      },
    }
  },
  created() {
    this.selectedPeriod = this.periodOptions[0]
    this.handleTranslationChange()
  },
  methods: {
    handleTranslationChange() {
      // Atualiza tradução do período selecionado ao mudar linguagem
      this.$root.$on('chooseLang', () => {
        const selectedPeriodIndex = this.periodOptions.map((option) => option.id).indexOf(this.selectedPeriod.id)
        this.selectedPeriod = this.periodOptions[selectedPeriodIndex]
      })
    },
    getDatasetCount(dataset) {
      return dataset.value.reduce((totalCount, item) => totalCount + item.count, 0)
    },
    emitPeriodChanged() {
      const periodFormat = 'yyyy-MM-dd'
      this.$emit('periodChanged', {
        periodStart: DateTime.fromISO(this.periodStart).toFormat(periodFormat),
        periodEnd: DateTime.fromISO(this.periodEnd).toFormat(periodFormat),
      })
    },
  },
  computed: {
    hasChartData() {
      return this.chartData?.datasets?.some((dataset) => dataset.value.length > 0)
    },
    userSelectedFormat() {
      return this.$store.state.auth.user.dateTimeFormat
    },
    periodStart() {
      return dateFormat(DateTime.now().toISO()).minus({ month: this.selectedPeriod?.months })
    },
    periodStartFormatted() {
      return this.periodStart.toFormat(getUserDateFormat(this.userSelectedFormat))
    },
    periodEnd() {
      return DateTime.now()
    },
    periodEndFormatted() {
      return dateFormat(this.periodEnd.toISO()).toFormat(getUserDateFormat(this.userSelectedFormat))
    },
    periodOptions() {
      return [
        {
          id: STATISTICS_CHART_PERIOD_TYPES.ONE_MONTH.id,
          label: this.$t('automation.statistics.chart.period.options.001'),
          months: STATISTICS_CHART_PERIOD_TYPES.ONE_MONTH.months,
        },
        {
          id: STATISTICS_CHART_PERIOD_TYPES.THREE_MONTHS.id,
          label: this.$t('automation.statistics.chart.period.options.002'),
          months: STATISTICS_CHART_PERIOD_TYPES.THREE_MONTHS.months,
        },
        {
          id: STATISTICS_CHART_PERIOD_TYPES.SIX_MONTHS.id,
          label: this.$t('automation.statistics.chart.period.options.003'),
          months: STATISTICS_CHART_PERIOD_TYPES.SIX_MONTHS.months,
        },
        {
          id: STATISTICS_CHART_PERIOD_TYPES.TWELVE_MONTHS.id,
          label: this.$t('automation.statistics.chart.period.options.004'),
          months: STATISTICS_CHART_PERIOD_TYPES.TWELVE_MONTHS.months,
        },
      ]
    },
    chartData() {
      return getGroupedChartData(this.statisticsProperties, this.selectedPeriod.months)
    },
  },
  watch: {
    selectedPeriod() {
      this.emitPeriodChanged()
    },
  },
}
</script>

<style scoped lang="scss">
.border-right-short {
  border-right: 1px solid #e9ecef;
}
.gap-20 {
  gap: 20px;
}

.gap-5 {
  gap: 5px;
}
.badge:empty {
  display: block;
}
.badge {
  border-radius: 0;
}

.w-130px {
  width: 130px;
}
</style>
